import { useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  FileText,
  Github,
  Globe,
  Instagram,
  Linkedin,
  Play,
  QrCode,
  ShoppingBag,
  Sparkles,
  Tag,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getStoredAppLocale, setStoredAppLocale } from "../lib/app-locale";
import { BUSINESS_INFO } from "../lib/business-info";

const messages = {
  ko: {
    eyebrow: "TMTT.Link NFC Tags",
    headline1: "모든 채널을",
    headline2: "한 번의 탭으로",
    sub: "LinkedIn, Instagram, GitHub, 디지털 명함까지. 오프라인 만남을 바로 온라인 연결로 전환하세요.",
    appTitle: "앱으로 링크를 관리하세요",
    appDesc: "태그에 연결할 프로필, 포트폴리오, SNS 주소를 TMTT.Link 앱에서 간단하게 관리할 수 있습니다.",
    shopTitle: "태그 선택하기",
    shopDesc: "목적에 맞는 NFC 태그를 고르고 바로 결제를 진행하세요.",
    buyInstagram: "인스타그램 태그",
    buyGithub: "깃허브 태그",
    buyLinkedin: "링크드인 태그",
    instantTitle: "탭 한 번으로 연결",
    instantDesc: "스마트폰을 가까이 대면 별도 앱 설치 없이 지정된 링크가 열립니다.",
    manageTitle: "링크 관리",
    manageDesc: "상황에 맞게 연결 주소를 업데이트하고 채널을 관리할 수 있습니다.",
    offlineTitle: "오프라인 전환",
    offlineDesc: "명함, 행사, 매장, 팝업 등 실제 접점을 디지털 액션으로 이어줍니다.",
    partners: "Trusted by builders and communities",
  },
  en: {
    eyebrow: "TMTT.Link NFC Tags",
    headline1: "All your channels,",
    headline2: "one tap away",
    sub: "Connect LinkedIn, Instagram, GitHub, digital business cards, and more from a single NFC tag.",
    appTitle: "Manage links in the app",
    appDesc: "Update profiles, portfolios, and social links connected to your TMTT.Link tag in one place.",
    shopTitle: "Choose your tag",
    shopDesc: "Pick the NFC tag that matches your use case and continue to checkout.",
    buyInstagram: "Instagram Tag",
    buyGithub: "GitHub Tag",
    buyLinkedin: "LinkedIn Tag",
    instantTitle: "Tap to connect",
    instantDesc: "Open your selected link instantly with a smartphone tap. No extra app required for visitors.",
    manageTitle: "Link management",
    manageDesc: "Update destinations and manage channels based on your campaign or context.",
    offlineTitle: "Offline conversion",
    offlineDesc: "Turn real-world interactions into digital follow-ups, profile visits, and portfolio views.",
    partners: "Trusted by builders and communities",
  },
};

const PARTNER_LOGOS = [
  { src: "/logos/fossasia.webp", alt: "FOSSASIA" },
  { src: "/logos/microsoft.webp", alt: "Microsoft" },
];

const purchaseLinks = [
  { key: "instagram", href: "/payment/instagram", icon: Instagram, className: "bg-pink-500 text-white hover:bg-pink-400" },
  { key: "linkedin", href: "/payment/linkedin", icon: Linkedin, className: "bg-blue-500 text-white hover:bg-blue-400" },
  { key: "github", href: "/payment/github", icon: Github, className: "bg-zinc-950 text-white hover:bg-zinc-800 border border-zinc-800" },
];

export default function Home() {
  const [lang, setLang] = useState(() => getStoredAppLocale());
  const t = messages[lang];

  const toggleLang = () =>
    setLang((currentLang) => {
      const nextLang = currentLang === "ko" ? "en" : "ko";
      setStoredAppLocale(nextLang);
      return nextLang;
    });

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.22),transparent_34%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.18),transparent_30%),linear-gradient(180deg,#09090b_0%,#09090b_50%,#020617_100%)]" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/75 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-zinc-950 shadow-sm">
              <Tag className="h-5 w-5" />
            </span>
            <span className="text-lg font-semibold tracking-tight">TMTT.Link</span>
          </a>
          <button
            onClick={toggleLang}
            title={lang === "ko" ? "Switch to English" : "한국어로 전환"}
            className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 text-sm font-medium text-zinc-200 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Globe className="h-4 w-4" />
            {lang === "ko" ? "EN" : "KO"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <section className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr] lg:items-stretch">
          <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/20 backdrop-blur md:p-10 lg:p-12">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-zinc-300">
              <Sparkles className="h-4 w-4 text-sky-300" />
              {t.eyebrow}
            </div>
            <h1 className="max-w-4xl text-5xl font-semibold tracking-tight text-white md:text-7xl lg:text-8xl">
              {t.headline1}
              <span className="block bg-gradient-to-r from-sky-300 via-violet-300 to-pink-300 bg-clip-text text-transparent">
                {t.headline2}
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-zinc-300 md:text-xl">{t.sub}</p>
            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {purchaseLinks.map((item) => {
                const Icon = item.icon;
                const label = item.key === "instagram" ? t.buyInstagram : item.key === "linkedin" ? t.buyLinkedin : t.buyGithub;
                return (
                  <a key={item.key} href={item.href} className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition-all hover:-translate-y-0.5 ${item.className}`}>
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                  </a>
                );
              })}
            </div>
          </div>

          <div className="grid gap-6">
            <div className="rounded-[2rem] border border-white/10 bg-white p-6 text-zinc-950 shadow-2xl shadow-black/20">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950 text-white">
                <QrCode className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">{t.appTitle}</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-600">{t.appDesc}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="https://apps.apple.com/kr/app/tmtt-link/id6757139702" target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center rounded-xl bg-zinc-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-zinc-800">
                  App Store
                </a>
                <a href="https://play.google.com/store/apps/details?id=net.tangibleidea.ttag.ttag" target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center rounded-xl border border-zinc-200 bg-white px-4 text-sm font-semibold text-zinc-950 transition-colors hover:bg-zinc-100">
                  Google Play
                </a>
              </div>
            </div>
            <div className="rounded-[2rem] border border-white/10 bg-zinc-900/80 p-6 shadow-xl shadow-black/20">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-zinc-200">
                <ShoppingBag className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">{t.shopTitle}</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-400">{t.shopDesc}</p>
              <a href="/payment" className="mt-6 inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                Shop tags
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-3">
          {[
            { icon: BadgeCheck, title: t.instantTitle, desc: t.instantDesc },
            { icon: FileText, title: t.manageTitle, desc: t.manageDesc },
            { icon: Play, title: t.offlineTitle, desc: t.offlineDesc },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-sky-200">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-400">{item.desc}</p>
              </div>
            );
          })}
        </section>

        <section className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <p className="text-sm font-medium text-zinc-400">{t.partners}</p>
            <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
              {PARTNER_LOGOS.map((logo) => (
                <img key={logo.src} src={logo.src} alt={logo.alt} className="h-12 w-auto max-w-[220px] object-contain grayscale brightness-0 invert opacity-45 transition-opacity hover:opacity-70 md:h-16" />
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl text-sm text-zinc-500">
          <Link
            to="/terms"
            className="inline-flex font-medium text-zinc-300 underline underline-offset-4 transition-colors hover:text-white"
          >
            이용약관
          </Link>

          {/*
            전자상거래법상 쇼핑몰 초기화면 하단에 노출해야 하는 필수정보 5개입니다.
            네이버페이 심사는 명칭(상호명, 대표자명 등)을 함께 표기해야 인정하므로
            라벨을 반드시 유지하세요. 값은 src/lib/business-info.js 에서 관리합니다.
          */}
          <dl className="mt-5 grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex gap-2">
              <dt className="shrink-0 font-medium text-zinc-300">상호명:</dt>
              <dd>{BUSINESS_INFO.companyName}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 font-medium text-zinc-300">대표자명:</dt>
              <dd>{BUSINESS_INFO.ceoName}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 font-medium text-zinc-300">사업자등록번호:</dt>
              <dd>
                <a
                  className="underline underline-offset-4 hover:text-zinc-300"
                  href={BUSINESS_INFO.businessNumberUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {BUSINESS_INFO.businessNumber}
                </a>
              </dd>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <dt className="shrink-0 font-medium text-zinc-300">주소:</dt>
              <dd>{BUSINESS_INFO.address}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 font-medium text-zinc-300">통신판매업신고번호:</dt>
              <dd>
                <a
                  className="underline underline-offset-4 hover:text-zinc-300"
                  href={BUSINESS_INFO.mailOrderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {BUSINESS_INFO.mailOrderNumber}
                </a>
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 font-medium text-zinc-300">전화번호:</dt>
              <dd>{BUSINESS_INFO.phone}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 font-medium text-zinc-300">전자우편주소:</dt>
              <dd>{BUSINESS_INFO.email}</dd>
            </div>
          </dl>

          <div className="mt-6 space-y-1.5 border-t border-white/5 pt-5 text-xs text-zinc-600">
            <p>{BUSINESS_INFO.companyNameEn} — overseas business registrations</p>
            <p>
              <span className="text-zinc-500">ABN (Australia 🇦🇺):</span>{" "}
              <a className="underline underline-offset-4 hover:text-zinc-400" href={BUSINESS_INFO.abnUrl} target="_blank" rel="noopener noreferrer">
                {BUSINESS_INFO.abn}
              </a>
            </p>
            <p>
              <span className="text-zinc-500">SCTA (Japan 🇯🇵):</span>{" "}
              <a className="break-all underline underline-offset-4 hover:text-zinc-400" href={BUSINESS_INFO.sctaUrl} target="_blank" rel="noopener noreferrer">
                {BUSINESS_INFO.sctaUrl}
              </a>
            </p>
            <p>🏠 Australia: {BUSINESS_INFO.addressAu}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
